import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { ServiceCard } from "@/components/ServiceCard";
import { useLanguage } from "@/i18n/LanguageContext";
import { SERVICES, WHY_ITEMS } from "@/data/content";
import { IMAGES } from "@/data/images";
import { ConsultButton } from "@/components/CtaButtons";

export const AboutPreview = () => {
  const { t } = useLanguage();
  const points = ["about.p1", "about.p2", "about.p3", "about.p4", "about.p5", "about.p6", "about.p7"];
  return (
    <section className="bg-offwhite py-24 md:py-32" data-testid="about-preview">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 md:px-8 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <Reveal>
            <SectionHeading eyebrow={t("about.eyebrow")} title={t("about.title")} />
            <p className="mt-6 max-w-lg text-base leading-relaxed text-charcoal/70">{t("about.body")}</p>
            <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {points.map((p) => (
                <li key={p} className="flex items-center gap-2.5 text-sm font-semibold text-charcoal/80">
                  <Check className="h-4 w-4 shrink-0 text-gold-dark" /> {t(p)}
                </li>
              ))}
            </ul>
            <Link
              to="/about"
              data-testid="about-meet-dn-link"
              className="group mt-10 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-gold-dark transition-colors hover:text-gold"
            >
              {t("cta.meetDn")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="relative lg:col-span-6">
          <Reveal delay={0.15}>
            <div className="relative">
              <img src={IMAGES.plan1} alt="Architectural planning drawings at D & N Constructions"
                loading="lazy" className="aspect-[4/3] w-full rounded-2xl object-cover" />
              <img src={IMAGES.int1} alt="Finished modern interior by D & N Constructions" loading="lazy"
                className="absolute -bottom-10 -left-4 hidden w-1/2 rounded-2xl border-8 border-offwhite object-cover shadow-2xl sm:block md:-left-10" />
              <div className="absolute -right-3 -top-3 -z-10 h-full w-full rounded-2xl border border-gold/40 md:-right-5 md:-top-5" aria-hidden="true" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export const ServicesPreview = () => {
  const { t } = useLanguage();
  return (
    <section className="bg-charcoal py-24 md:py-32" data-testid="services-preview">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-8">
            <SectionHeading dark eyebrow={t("services.eyebrow")} title={t("services.title")} sub={t("services.sub")} />
            <Link to="/services" data-testid="services-view-all-link"
              className="group mb-1 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-gold transition-colors hover:text-gold-soft">
              {t("nav.services")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={(i % 3) * 0.08}>
              <ServiceCard service={s} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const WhyUs = () => {
  const { t } = useLanguage();
  return (
    <section className="bg-white py-24 md:py-32" data-testid="why-dn">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Reveal><SectionHeading eyebrow={t("why.eyebrow")} title={t("why.title")} /></Reveal>
        <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {WHY_ITEMS.map((item, i) => (
            <Reveal key={item} delay={(i % 4) * 0.05}>
              <div data-testid={`why-item-${i}`}
                className="flex h-full items-center gap-3 rounded-xl border border-charcoal/10 bg-offwhite px-5 py-4 transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-gold-dark/40">
                <Check className="h-4 w-4 shrink-0 text-gold-dark" />
                <span className="text-sm font-semibold text-charcoal/80">{item}</span>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1} className="mt-12"><ConsultButton testid="why-consult-button" /></Reveal>
      </div>
    </section>
  );
};
